import type { Mood } from '@shkola/api';
import { DEEP, GRAD, type Grad, SUBJECT_GRAD, type ToneName } from '@shkola/design';
import type { CSSProperties } from 'react';

/*
 * «Форми» (28.10, his moodboard: «Creative Leaders Circle», «v.Blend», the language app with its
 * triangles and flowers, the timeline cards, the colour pills with stars, the ManyChat line drawings,
 * the calendar with scribbled days): warm paper, flat colour with no gradients, a light grotesk, and a
 * language of plain shapes — each subject has its shape, the cards of what to do carry a big one, the
 * moods are shapes with a face; done days are scribbled over; one line drawing with an arrow.
 */

/**
 * The paper, the ink and the flat colours of «Форми». The ink and the dark of buttons come from the look
 * (styles.css): a deep pine in «Форми», a near-black violet in «Форми 2» (his «тёмно-тёмно-фиолетовый»);
 * the softer inks are the same dark with transparency, so they sit on a coloured card too — never grey.
 */
export const INK = 'var(--sh-ink)';
export const INK_SOFT = 'var(--sh-ink-soft)';
export const INK_FAINT = 'var(--sh-ink-faint)';
export const DARK = 'var(--sh-dark)';
export const PAPER = '#F5F3EF';
export const LINE = '#E4E0D7';

// the gradients live in `@shkola/design` (forms2.ts), shared with the app
export { DEEP, GRAD, type Grad, SUBJECT_GRAD };

export const FLAT = {
  lime: '#DDF56B',
  mint: '#A9E4C3',
  sage: '#D3E4DB',
  aqua: '#B7ECE3',
  sky: '#CFE3F8',
  cobalt: '#4B5FE0',
  lavender: '#D9C3F8',
  pink: '#F8B7D3',
  blush: '#F7D5DE',
  salmon: '#FFC3AE',
  orange: '#FF6B3D',
  apricot: '#FFD6A5',
  butter: '#FFE36E',
  cream: '#F6EBCB',
  stone: '#E8E5DE',
  pine: '#115C47',
} as const;

export type Flat = keyof typeof FLAT;

/**
 * «Форми 2» (28.10, his «с градиентами, а не чистыми цветами»; refs the soft widget cards and the board of
 * «weihu»): each colour as a soft gradient from light to its tone, a glow of white in the corner, the
 * words in the colour's own ink (`GRAD`; the pictures' shapes in `DEEP`). The ground of a soft card:
 * the gradient from light to its tone and a white glow in its top corner (his «предыдущая версия по
 * градиентам была намного лучше», 28.10 — the corner-light scheme stays).
 */
export const grad = (k: Grad) =>
  `radial-gradient(90% 70% at 0% 0%, rgb(255 255 255 / 0.55) 0%, rgb(255 255 255 / 0) 60%), linear-gradient(165deg, ${GRAD[k].from} 0%, ${GRAD[k].to} 100%)`;

export type ShapeKind =
  | 'circle'
  | 'triangle'
  | 'square'
  | 'diamond'
  | 'flower'
  | 'star'
  | 'burst'
  | 'plus'
  | 'dome'
  | 'pill';

/** A subject's colour and shape by its tone (the tone comes from `subjectLook`). */
export const SUBJECT_SHAPE: Record<ToneName, { fill: Flat; mark: Flat; shape: ShapeKind }> = {
  blue: { fill: 'sky', mark: 'cobalt', shape: 'circle' },
  violet: { fill: 'lavender', mark: 'cobalt', shape: 'flower' },
  pink: { fill: 'pink', mark: 'orange', shape: 'burst' },
  red: { fill: 'salmon', mark: 'orange', shape: 'diamond' },
  orange: { fill: 'apricot', mark: 'orange', shape: 'square' },
  yellow: { fill: 'butter', mark: 'orange', shape: 'star' },
  green: { fill: 'mint', mark: 'pine', shape: 'triangle' },
  teal: { fill: 'aqua', mark: 'pine', shape: 'dome' },
  grey: { fill: 'stone', mark: 'pine', shape: 'plus' },
};

const burst = (() => {
  const pts = Array.from({ length: 16 }, (_, i) => {
    const a = (i / 16) * Math.PI * 2 - Math.PI / 2;
    const r = i % 2 === 0 ? 19.5 : 12.5;
    return `${(20 + Math.cos(a) * r).toFixed(2)},${(20 + Math.sin(a) * r).toFixed(2)}`;
  });
  return pts.join(' ');
})();

/** The body of a shape in a 40×40 box. */
function Body({ kind, fill }: { kind: ShapeKind; fill: string }) {
  const round = { stroke: fill, strokeWidth: 5, strokeLinejoin: 'round' as const };
  switch (kind) {
    case 'circle':
      return <circle cx="20" cy="20" r="19" fill={fill} />;
    case 'triangle':
      return <path d="M20 4.5 36.5 35H3.5Z" fill={fill} {...round} />;
    case 'square':
      return <rect x="2" y="2" width="36" height="36" rx="7" fill={fill} />;
    case 'diamond':
      return <path d="M20 3.5 36.5 20 20 36.5 3.5 20Z" fill={fill} {...round} />;
    case 'flower':
      return (
        <g fill={fill}>
          <circle cx="20" cy="10.5" r="9.5" />
          <circle cx="29.5" cy="20" r="9.5" />
          <circle cx="20" cy="29.5" r="9.5" />
          <circle cx="10.5" cy="20" r="9.5" />
          <circle cx="20" cy="20" r="9" />
        </g>
      );
    case 'star':
      return (
        <path
          d="M20 1C21.6 13.2 26.8 18.4 39 20 26.8 21.6 21.6 26.8 20 39 18.4 26.8 13.2 21.6 1 20 13.2 18.4 18.4 13.2 20 1Z"
          fill={fill}
        />
      );
    case 'burst':
      return <polygon points={burst} fill={fill} {...round} strokeWidth={2.5} />;
    case 'plus':
      return (
        <g fill={fill}>
          <rect x="13.5" y="2" width="13" height="36" rx="6.5" />
          <rect x="2" y="13.5" width="36" height="13" rx="6.5" />
        </g>
      );
    case 'dome':
      return <path d="M2 30a18 18 0 0 1 36 0v4a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4Z" fill={fill} />;
    case 'pill':
      return <rect x="1" y="11" width="38" height="18" rx="9" fill={fill} />;
  }
}

export function Shape({
  kind,
  color,
  size = 40,
  className = '',
  style,
}: {
  kind: ShapeKind;
  color: string;
  size?: number;
  className?: string;
  style?: CSSProperties;
}) {
  return (
    <svg
      viewBox="0 0 40 40"
      width={size}
      height={size}
      aria-hidden
      focusable="false"
      className={`shrink-0 overflow-visible ${className}`}
      style={style}
    >
      <Body kind={kind} fill={color} />
    </svg>
  );
}

/** A day done, scribbled over in pen (ref. the calendar with scribbled days). */
export function Scribble({ className = '' }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 48 48"
      aria-hidden
      focusable="false"
      preserveAspectRatio="none"
      className={`pointer-events-none ${className}`}
    >
      <path
        d="M9 15 33 9 8 22 38 15 10 30 39 23 12 37 36 31 16 41"
        fill="none"
        stroke={INK}
        strokeWidth="2.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** One line drawn without lifting the pen, looping, ending in an arrow (ref. ManyChat). */
export function Doodle({ className = '', color = INK }: { className?: string; color?: string }) {
  return (
    <svg
      viewBox="0 0 220 110"
      aria-hidden
      focusable="false"
      className={`pointer-events-none overflow-visible ${className}`}
    >
      <path
        d="M4 92C28 100 46 72 40 54 34 36 12 44 20 62 30 84 74 86 92 58 106 36 92 16 78 26 64 38 88 64 120 60 152 56 168 30 200 22"
        fill="none"
        stroke={color}
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M188 14 201 21 192 33"
        fill="none"
        stroke={color}
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="4" cy="92" r="3.5" fill={color} />
    </svg>
  );
}

/** A mood as a shape with a face: each its own colour and form. */
export const MOOD_SHAPE: Record<Mood, { shape: ShapeKind; fill: Flat }> = {
  great: { shape: 'flower', fill: 'lime' },
  good: { shape: 'circle', fill: 'mint' },
  okay: { shape: 'square', fill: 'butter' },
  sad: { shape: 'dome', fill: 'sky' },
  angry: { shape: 'burst', fill: 'orange' },
  anxious: { shape: 'diamond', fill: 'lavender' },
};

export function MoodShape({ mood, size = 40 }: { mood: Mood; size?: number }) {
  const { shape, fill } = MOOD_SHAPE[mood];
  const line = {
    stroke: INK,
    strokeWidth: 2.2,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    fill: 'none',
  };
  // the dome sits low: its face lower too
  const dy = shape === 'dome' ? 5 : 0;
  return (
    <svg
      viewBox="0 0 40 40"
      width={size}
      height={size}
      aria-hidden
      focusable="false"
      className="shrink-0 overflow-visible"
    >
      <Body kind={shape} fill={FLAT[fill]} />
      <g transform={`translate(0 ${dy})`}>
        {mood === 'great' ? (
          <>
            <path d="M13 18q2.2-3 4.4 0M22.6 18q2.2-3 4.4 0" {...line} />
            <path d="M14 23q6 6 12 0" {...line} />
          </>
        ) : (
          <>
            {mood === 'angry' && <path d="M13 14.5l4.5 2M27 14.5l-4.5 2" {...line} />}
            <circle cx="15.5" cy="18.5" r="1.7" fill={INK} />
            <circle cx="24.5" cy="18.5" r="1.7" fill={INK} />
            {mood === 'good' && <path d="M15 24q5 4 10 0" {...line} />}
            {mood === 'okay' && <path d="M15.5 25h9" {...line} />}
            {mood === 'sad' && <path d="M15 26q5-4 10 0" {...line} />}
            {mood === 'angry' && <path d="M15.5 26q4.5-3 9 0" {...line} />}
            {mood === 'anxious' && (
              <path d="M14.5 25.5q1.4-1.6 2.8 0t2.8 0t2.8 0t2.8 0" {...line} />
            )}
          </>
        )}
      </g>
    </svg>
  );
}
